import { I18n } from "@assets/strings";
import React from "react";
import { useHistory } from "@core/routing/useHistory";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { useServices } from "@core/servicesContext";
import { useEmailVerification } from "@domain/auth/hooks";
import { usePromise } from "@utils/hooks";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { ErrorContainer } from "@ui/components/containers/errorContainer";
import { gap } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";
import { OnboardingTitle } from "@ui/pages/loginPage/onboarding/common";

interface EmailVerificationFormProps extends React.HTMLAttributes<HTMLDivElement> {
	oobCode: string;
}
export const EmailVerificationForm: React.FC<EmailVerificationFormProps> = ({ oobCode, ...htmlProps }) => {
	const { authService } = useServices();
	const history = useHistory();
	const { loading, error } = usePromise(async () => {
		await authService.verify(oobCode);
		await waitFor(async () => {
			return await authService.checkUserVerified();
		}, 500);
		if (!authService.hasUser.get()) {
			createToast(I18n.t("success"), I18n.t("verify.success"), "success");
		}
		history.push("/");
	}, []);

	const { sendEmail, loading: verifyLoading, sent } = useEmailVerification();

	return (
		<Container {...htmlProps}>
			<OnboardingTitle>{I18n.t("verify.title")}</OnboardingTitle>
			{loading ? (
				<ClipLoader size={60} />
			) : error ? (
				<>
					<ErrorContainer>
						{(() => {
							switch (error.code) {
								case "auth/expired-action-code":
									return I18n.t("verify.error.expired");
								case "auth/invalid-action-code":
									return I18n.t("verify.error.invalid");
								case "auth/user-disabled":
									return I18n.t("verify.error.disabled");
								case "auth/user-not-found":
									return I18n.t("verify.error.not_found");
								default:
									return I18n.t("error");
							}
						})()}
					</ErrorContainer>
					<LoadingButton disabled={sent} loading={verifyLoading} onClick={sendEmail}>
						{I18n.t("onboarding.verify.retry")}
					</LoadingButton>
				</>
			) : null}
		</Container>
	);
};

function waitFor(predicate: () => Promise<boolean>, delay: number): Promise<void> {
	return new Promise((resolve) => {
		const timeoutFunction = async (): Promise<void> => {
			if (await predicate()) {
				resolve();
				clearTimeout(timeout);
			} else {
				timeout = setTimeout(timeoutFunction, delay);
			}
		};
		let timeout = setTimeout(async () => {
			if (await predicate()) {
				resolve();
			} else {
				timeout = setTimeout(timeoutFunction, delay);
			}
		}, delay);
	});
}

const Container = styled.div`
	${gap(50)}
	display: flex;
	align-items: center;
	flex-direction: column;
`;
